import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import makeStyles from '@material-ui/core/styles/makeStyles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import classnames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { color } from '../../app/theme';
import IconDelete from '../../assets/images/Icons/IconDelete';
import IconEdit from '../../assets/images/Icons/IconEdit';
import IconLink from '../../assets/images/Icons/IconLink';
import IconSend from '../../assets/images/Icons/IconSend';
import IconConfirmCard from '../../assets/images/svg/ic-card-confirm.svg';
import IconImageCard from '../../assets/images/svg/ic-card-image.svg';
import IconOptionalCard from '../../assets/images/svg/ic-card-item.svg';
import IconListCard from '../../assets/images/svg/ic-card-list.svg';
import IconFile from '../../assets/images/svg/ic_folder.svg';
import IconTextCard from '../../assets/images/svg/icon_card template_list.svg';
import imgAudio from '../../assets/images/svg/img-file-audio.svg';
import imgDoc from '../../assets/images/svg/img-file-doc.svg';
import imgVideo from '../../assets/images/svg/img-file-video.svg';
import Tooltip from '../shared/Tooltip';

import { SnackBarType } from '../../app/types';
import utils from '../../app/utils';
import { enqueueSnackbar } from '../../features/notifierSlice';
import useDOMById from '../../hooks/useDOMById';
import {
  HashtagUrlText,
  WaTempPlaceholderUrlText,
} from '../shared/HashtagText';
import ConfirmCard from './previews/ConfirmCard';
import ImageCard from './previews/ImageCard';
import OptionalCard from './previews/OptionalCard';
import WhatsAppListCard from './previews/WhatsAppListCard';

const useStyles = makeStyles(
  () => ({
    root: {
      display: 'grid',
      gridTemplateColumns: '44px 1fr',
      gridTemplateRows: '18px 18px',
      columnGap: '8px',
      rowGap: '8px',
      padding: 16,
      '&:hover': {
        backgroundColor: color.$19,
      },
    },
    templateIcon: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gridColumn: '1 / 2',
      gridRow: '1 / 3',
      backgroundColor: color.$8,
      '& > img': {
        display: 'block',
      },
    },
    nameBlock: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gridColumn: '2 / 3',
      gridRow: '1 / 2',
      fontSize: 17,
      lineHeight: 1,
      color: color.$13,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      '& > div.operation': {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
      },
    },
    name: {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
    note: {
      gridColumn: '2 / 3',
      fontSize: 13,
      lineHeight: 1,
      color: color.$12,
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
    noteCategory: {
      gridColumn: '2 / 3',
      fontSize: 13,
      lineHeight: 1,
      color: color.$10,
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
    icon: {
      cursor: 'pointer',
      margin: '0 4px 0 0',
      '&:last-child': {
        margin: 0,
      },
      '& > svg': {
        display: 'block',
      },
    },
    description: {
      gridColumn: '2 / 3',
      gridRow: '2 / 3',
      fontSize: 13,
      lineHeight: 1,
      color: color.$10,
    },
    tootip: {
      top: '6px',
    },
    previewSpace: {
      top: -16,
      left: 0,
      position: 'absolute',
      paddingRight: 8,
    },
    previewBlock: {
      top: -16,
      left: 0,
      zIndex: 1,
      maxWidth: 420,
      display: 'flex',
      backgroundColor: color.$8,
      padding: 16,
      borderRadius: 12,
      overflowX: 'auto',
    },
    'previewBlock--small': {
      width: 192,
    },
    'previewBlock-hidden': {
      display: 'none',
    },
  }),
  {
    name: 'DefaultTemplateItem',
  },
);

let hiddenTimerArray = [];

function DefaultTemplateItem({
  data,
  onSelectDefaultTemplate,
  onEditDefaultTemplate,
  onDeleteDefaultTemplate,
  isEdit,
  isSend,
  isCopy,
  isWhatsappTemplate,
}) {
  const classes = useStyles();
  const contentDOM = useDOMById('default-template-content');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const rootRef = useRef(null);
  const previewRef = useRef(null);
  const [isHover, setisHover] = useState(false);
  const [previewStyles, setPreviewStyles] = useState({
    top: 0,
    left: 0,
  });
  const isWidthLessThan1280 = useMediaQuery('(max-width:1279px)');

  let cardImage = null;
  switch (data.templateType || data?.contentType) {
    case 'card':
      cardImage = IconOptionalCard;
      break;
    case 'confirm':
      cardImage = IconConfirmCard;
      break;
    case 'list':
    case 'waList':
    case 'whatsappTemplate':
      cardImage = IconListCard;
      break;
    case 'image':
      cardImage = IconImageCard;
      break;
    case 'text':
      cardImage = IconTextCard;
      break;

    case 'application/x-audio':
    case 'application/x-video':
    case 'application/x-file':
    case 'application/x-image':
      cardImage = IconFile;
      break;
    default:
  }

  useEffect(() => {
    return () => {
      hiddenTimerArray.forEach(timer => {
        clearTimeout(timer);
      });
      hiddenTimerArray = [];
    };
  }, []);

  useEffect(() => {
    if (isHover) {
      setPreviewStyles({
        top: rootRef.current.offsetTop - contentDOM.scrollTop,
        left: -1 * previewRef.current.offsetWidth,
      });
    }
  }, [rootRef, previewRef, isHover, contentDOM]);

  function handleMouseEnter(id) {
    if (hiddenTimerArray[id]) {
      clearTimeout(hiddenTimerArray[id]);
    }
    setisHover(true);
  }

  function handleMouseLeave() {
    setisHover(false);
  }

  function handleCopyDefaultTemplate(id) {
    utils.copyToClipboard(id, () => {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.SUCCESS,
          message: t('copyTemplateIdSuccess'),
        }),
      );
    });
  }

  const previewContentComponent = () => {
    if (data?.templateType === 'text') {
      return (
        <div
          style={{ maxHeight: 120, width: 360 }}
          dangerouslySetInnerHTML={{
            __html: isWhatsappTemplate
              ? WaTempPlaceholderUrlText(data?.data?.content)
              : HashtagUrlText(data?.data?.content),
          }}
        />
      );
    }

    switch (data?.contentType) {
      case 'application/x-audio':
        return <img width="100" src={imgAudio} alt="imgAudio" />;
      case 'application/x-video':
        return <img width="100" src={imgVideo} alt="imgVideo" />;
      case 'application/x-file':
        return <img width="100" src={imgDoc} alt="imgDoc" />;
      case 'application/x-image':
        return <img width="245" src={data?.data?.url} alt="img" />;
      default:
        return data?.data?.elements?.map((elememt, index) => {
          switch (data.templateType) {
            case 'card':
              return (
                <OptionalCard
                  key={`default-template-preview-card-${index}`}
                  data={elememt}
                  messageIndex={0}
                  index={index}
                  width={124}
                />
              );
            case 'confirm':
              return (
                <ConfirmCard
                  key={`default-template-preview-confirm-${index}`}
                  elements={data?.data?.elements}
                  width={178}
                />
              );
            case 'image':
              return (
                <ImageCard
                  key={`default-template-preview-image-${index}`}
                  data={elememt}
                  messageIndex={0}
                  index={index}
                  width={124}
                />
              );
            case 'waList':
              return (
                <WhatsAppListCard
                  key={`default-template-preview-wa-list-card-${index}`}
                  data={elememt}
                  messageIndex={0}
                  index={index}
                  width={124}
                />
              );
            default:
              return null;
          }
        });
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setisHover(false)}>
      <div
        ref={rootRef}
        className={classes.root}
        onMouseEnter={() => handleMouseEnter(data._id)}
        onMouseLeave={() => handleMouseLeave(data._id)}
      >
        <div className={classes.templateIcon}>
          <img src={cardImage} alt={data.templateType} />
        </div>
        <div className={classes.nameBlock}>
          <div className={classes.name} title={data.name}>
            {data.name}
          </div>
          {isHover && (
            <div className="operation">
              {isCopy && (
                <Tooltip
                  placement="top"
                  title={t('copyTempldateId')}
                  detailClasses={{
                    tooltipPlacementTop: classes.tootip,
                  }}
                >
                  <div
                    className={classes.icon}
                    onClick={() => handleCopyDefaultTemplate(data.objectId)}
                  >
                    <IconLink
                      id={`copy-template-${data.objectId}`}
                      width={20}
                      height={20}
                      color={color.$10}
                      hoverColor={color.$1}
                    />
                  </div>
                </Tooltip>
              )}

              {isEdit && (
                <Tooltip
                  title={t('delete-default-template-title')}
                  placement="top"
                  detailClasses={{
                    tooltipPlacementTop: classes.tootip,
                  }}
                >
                  <div
                    className={classes.icon}
                    onClick={onDeleteDefaultTemplate}
                  >
                    <IconDelete
                      id={`delete-template-${data._id}`}
                      width={20}
                      height={20}
                      color={color.$10}
                      hoverColor={color.$1}
                    />
                  </div>
                </Tooltip>
              )}
              {isEdit && (
                <Tooltip
                  title={t('edit-default-template')}
                  placement="top"
                  detailClasses={{
                    tooltipPlacementTop: classes.tootip,
                  }}
                >
                  <div className={classes.icon} onClick={onEditDefaultTemplate}>
                    <IconEdit
                      id={`edit-template-${data._id}`}
                      width={20}
                      height={20}
                      color={color.$10}
                      hoverColor={color.$1}
                    />
                  </div>
                </Tooltip>
              )}
              {isSend && (
                <Tooltip
                  title={
                    isWhatsappTemplate
                      ? t('use-wa-template')
                      : t('use-default-template')
                  }
                  placement="top"
                  detailClasses={{
                    tooltipPlacementTop: classes.tootip,
                  }}
                >
                  <div
                    className={classes.icon}
                    onClick={onSelectDefaultTemplate}
                  >
                    <IconSend
                      id={`use-template-${data._id}`}
                      width={20}
                      height={20}
                      color={color.$10}
                      hoverColor={color.$1}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          )}
        </div>
        {data.note && <div className={classes.note}>{data.note}</div>}
        {data.category && (
          <div className={classes.noteCategory}>
            {t(`whatsappTemplate.${data.category}`)}
          </div>
        )}
        <div className={classes.description}>
          {t(
            `types.message-template-type.${
              data?.templateType ||
              data?.data?.templateType ||
              data?.contentType
            }`,
          )}
          {(data?.expiredAt?.iso || data?.expiredAt) &&
            t(`templates.expiredAt`, {
              date: moment(data?.expiredAt?.iso || data?.expiredAt).format(
                'YYYY-MM-DD HH:mm',
              ),
            })}
        </div>
        <div
          ref={previewRef}
          style={previewStyles}
          className={classnames(classes.previewSpace, {
            [classes['previewBlock-hidden']]: !isHover,
          })}
          onMouseEnter={() => handleMouseEnter(data._id)}
        >
          <div
            className={classnames(classes.previewBlock, {
              [classes['previewBlock--small']]: isWidthLessThan1280,
            })}
          >
            {previewContentComponent()}
          </div>
        </div>
      </div>
    </ClickAwayListener>
  );
}

DefaultTemplateItem.defaultProps = {};

DefaultTemplateItem.propTypes = {
  data: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
};

export default DefaultTemplateItem;
